import { api } from './client';

export type TicketStatus =
  | 'new' | 'open' | 'pending_customer' | 'on_hold' | 'resolved' | 'closed' | 'cancelled';

export type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4';

export type SlaState = 'green' | 'yellow' | 'orange' | 'red' | 'ok';

export interface TicketBrief {
  id: string;
  number: number;
  subject: string;
  body_excerpt: string | null;
  redmine_issue_id?: number | null;
  redmine_url?: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  requester_email: string | null;
  requester_name: string | null;
  assignee_id: string | null;
  assignee_name: string | null;
  assignee_email: string | null;
  created_at: string;
  updated_at: string;
  sla_first_response_due_at: string | null;
  sla_resolution_due_at: string | null;
  first_responded_at: string | null;
  resolved_at: string | null;
  closed_at: string | null;
  sla_state: SlaState | null;
  sla_seconds_left: number | null;
  sla_label: string | null;
  organization_id: string | null;
  organization_name: string | null;
  organization_inn: string | null;
  contract_active: boolean | null;
  contract_status: 'active' | 'expiring_soon' | 'expired' | 'inactive' | null;
  contract_days_left: number | null;
  contract_ends_at: string | null;
  installation_id: string | null;
  installation_name: string | null;
  has_new_client_message: boolean;
  support_line: number | null;
  ai_autopilot_enabled: boolean;
  project_status?: 'support' | 'pilot' | 'no_support' | null;
}

export interface Message {
  id: string;
  ticket_id: string;
  body: string;
  is_internal: boolean;
  ai_generated: boolean;
  author_email: string | null;
  author_name: string | null;
  created_at: string;
}

export interface TicketRating {
  score: number;
  comment: string | null;
  rated_by_name: string | null;
  created_at: string;
}

export interface TicketDetail extends TicketBrief {
  body: string;
  messages: Message[];
  rating: TicketRating | null;
}

export interface QueueQuery {
  /** Один статус или несколько через запятую («new,open») — API отдаёт объединение. */
  status?: TicketStatus | string;
  hide_closed?: boolean;
  priority?: TicketPriority;
  q?: string;
  assignee?: string;            // 'me' | 'unassigned' | <user_id>
  organization_id?: string;
  sort_by?: 'created_at' | 'updated_at' | 'priority' | 'status' | 'number';
  sort_dir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface TicketListResponse {
  items: TicketBrief[];
  total: number;
}

export interface TicketCreatePayload {
  subject: string;
  body?: string;
  priority?: TicketPriority;
  requester_email?: string;
  requester_name?: string;
  organization_id?: string | null;
  installation_id?: string | null;
}

export const ticketsApi = {
  list: (params: QueueQuery = {}) =>
    api.get<TicketListResponse>('/api/v1/tickets', { params }).then(r => r.data),

  assign: (id: string, payload: { assignee_id?: string | null; self_assign?: boolean }) =>
    api.post<TicketDetail>(`/api/v1/tickets/${id}/assign`, payload).then(r => r.data),

  get: (id: string) =>
    api.get<TicketDetail>(`/api/v1/tickets/${id}`).then(r => r.data),

  create: (payload: TicketCreatePayload) =>
    api.post<TicketDetail>('/api/v1/tickets', payload).then(r => r.data),

  update: (id: string, payload: Partial<{
    subject: string; status: TicketStatus; priority: TicketPriority;
    installation_id: string | null;
    support_line: number | null;
    ai_autopilot_enabled: boolean;
  }>) =>
    api.patch<TicketDetail>(`/api/v1/tickets/${id}`, payload).then(r => r.data),

  postMessage: (id: string, payload: { body: string; is_internal?: boolean }) =>
    api.post<Message>(`/api/v1/tickets/${id}/messages`, payload).then(r => r.data),

  toggleMessageVisibility: (ticketId: string, messageId: string) =>
    api.post<Message>(`/api/v1/tickets/${ticketId}/messages/${messageId}/toggle-visibility`).then(r => r.data),

  bulkAction: (ids: string[], action: string, value?: string) =>
    api.post<{ updated: number; skipped: number }>(
      `/api/v1/tickets/_bulk`, { ids, action, value }).then(r => r.data),

  reopen: (ticketId: string) =>
    api.post<TicketDetail>(`/api/v1/tickets/${ticketId}/reopen`).then(r => r.data),

  confirmClose: (ticketId: string) =>
    api.post<TicketDetail>(`/api/v1/tickets/${ticketId}/confirm-close`).then(r => r.data),

  stats: () =>
    api.get<Record<string, number>>('/api/v1/tickets/_stats').then(r => r.data),
};
